import { Stack, Input, Button, Text } from '@ltvco/refresh-lib/theme';
import { useScreenSize } from '@ltvco/refresh-lib/utils';
import { FcraWarning } from '@ltvco/refresh-lib/v1';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';

interface NameSearchForm {
  firstName: string;
  lastName: string;
}

export interface SearchNameFormProps {
  currentFirstName?: string;
  currentLastName?: string;
  // eslint-disable-next-line no-unused-vars
  onSubmit: (firstName: string, lastName: string) => void;
}

export const SearchNameForm = ({
  currentFirstName,
  currentLastName,
  onSubmit,
}: SearchNameFormProps) => {
  const { isMobile } = useScreenSize();

  const { register, handleSubmit, watch, reset } = useForm<NameSearchForm>({
    defaultValues: {
      firstName: currentFirstName || '',
      lastName: currentLastName || '',
    },
  });

  // Update form when props change (for programmatic name changes)
  useEffect(() => {
    reset({
      firstName: currentFirstName || '',
      lastName: currentLastName || '',
    });
  }, [currentFirstName, currentLastName, reset]);

  const watchedValues = watch();
  const isSubmitDisabled =
    !watchedValues.firstName?.trim() && !watchedValues.lastName?.trim();

  // Handle form submission
  const handleFormSubmit = (data: NameSearchForm) => {
    onSubmit(data.firstName, data.lastName);
  };

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)}>
      <Text fontWeight="bold" mb={2}>
        Search Another Name
      </Text>
      <Stack
        direction={isMobile ? 'column' : 'row'}
        spacing={2}
        alignItems={isMobile ? 'stretch' : 'center'}
        sx={{ mb: 2, width: '100%' }}
      >
        <Input
          {...register('firstName')}
          placeholder="First Name"
          size="small"
          sx={{
            flex: 1,
            '& .MuiInputBase-root': {
              backgroundColor: 'transparent',
            },
            '& .MuiOutlinedInput-root': {
              backgroundColor: 'transparent',
            },
            '& fieldset': {
              borderRadius: '32px',
            },
          }}
        />
        <Input
          {...register('lastName')}
          placeholder="Last Name"
          size="small"
          sx={{
            flex: 1,
            '& .MuiInputBase-root': {
              backgroundColor: 'transparent',
            },
            '& .MuiOutlinedInput-root': {
              backgroundColor: 'transparent',
            },
            '& fieldset': {
              borderRadius: '32px',
            },
          }}
        />
        <Button
          variant="contained"
          type="submit"
          disabled={isSubmitDisabled}
          sx={{
            ...(isMobile && { width: '100%' }),
          }}
        >
          Search
        </Button>
      </Stack>
      <FcraWarning
        fcraLink="https://www.beenverified.com/about/fcra/"
        tosLink="https://www.beenverified.com/faq/terms-conditions/"
        brandName="BeenVerified"
      />
    </form>
  );
};
